import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ChevronUp, X, ScanLine, BedDouble, CalendarPlus,
  FileSpreadsheet, Users, BookOpen, Sparkles,
} from "lucide-react";

const STORAGE_KEY = "xenia_quickstart_collapsed_v1";
const DISMISS_KEY = "xenia_quickstart_dismissed_v1";

const STEPS = [
  {
    n: 1,
    Icon: BedDouble,
    title: "Aggiungi le tue camere",
    desc: "Vai su «Camere» e crea le stanze/appartamenti della tua struttura (nome, tipo, capienza). Serve una sola volta.",
    cta: { to: "/app/rooms", label: "Vai alle camere" },
  },
  {
    n: 2,
    Icon: ScanLine,
    title: "Registra il tuo primo ospite",
    desc: "Scansiona la carta d'identità con il tasto centrale (📷) o compila a mano. L'app estrae i dati, tu confermi.",
    cta: { to: "/app/scanner", label: "Scansiona documento" },
  },
  {
    n: 3,
    Icon: CalendarPlus,
    title: "Crea una prenotazione",
    desc: "Dal Calendario tocca «Nuova prenotazione». Puoi scegliere ospite e azienda dalla rubrica già salvata — non ricompilarli più.",
    cta: { to: "/app/calendar", label: "Apri calendario" },
  },
  {
    n: 4,
    Icon: FileSpreadsheet,
    title: "Invia dati a Polizia / ISTAT",
    desc: "A fine giornata vai su «Alloggiati» e invia con un click. L'app prepara il file conforme automaticamente.",
    cta: { to: "/app/alloggiati", label: "Vai ad Alloggiati" },
  },
  {
    n: 5,
    Icon: Users,
    title: "Rubrica riutilizzabile",
    desc: "Ospiti e aziende restano salvati per sempre. La prossima volta li ritrovi con un click, senza doverli reinserire.",
    cta: { to: "/app/guests", label: "Apri rubrica" },
  },
  {
    n: 6,
    Icon: BookOpen,
    title: "Serve aiuto?",
    desc: "Nel menu «Altro» → «Guida» trovi tutorial dettagliati, video e un modulo contatti per ogni dubbio.",
    cta: { to: "/app/help", label: "Apri la guida" },
  },
];

export default function QuickStartGuide() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_KEY) === "true");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "true");

  if (dismissed) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(STORAGE_KEY, String(next));
  };
  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, "true");
  };

  return (
    <div
      className="mb-8 border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50/40 rounded-2xl overflow-hidden shadow-[0_2px_20px_rgba(37,99,235,0.08)] relative"
      data-testid="quickstart-guide"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Chiudi definitivamente la guida"
        className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white text-slate-400 hover:text-slate-700 transition-colors z-10"
        data-testid="quickstart-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="w-full flex items-center gap-3 px-5 py-4 pr-12 text-left hover:bg-blue-50/60 transition-colors"
        data-testid="quickstart-toggle"
      >
        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_rgba(37,99,235,0.4)]">
          <Sparkles className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-lg text-slate-900 leading-tight">
            Guida rapida · {STEPS.length} passi per iniziare
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {collapsed ? "Tocca per aprire le istruzioni passo-passo" : "Segui i passi in ordine o salta a quello che ti serve"}
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 pt-1 grid gap-3 sm:grid-cols-2" data-testid="quickstart-steps">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="flex gap-3 p-4 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
              data-testid={`quickstart-step-${s.n}`}
            >
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-heading font-bold text-sm flex items-center justify-center">
                  {s.n}
                </div>
                <s.Icon className="w-5 h-5 text-slate-400 mt-2" strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm text-slate-900 leading-tight mb-1">
                  {s.title}
                </div>
                <p className="text-xs text-slate-600 leading-snug mb-2">{s.desc}</p>
                <Link
                  to={s.cta.to}
                  className="inline-flex items-center text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline"
                >
                  {s.cta.label} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
